import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { RUN_SHEET } from "@/data/runsheet";
import { Seo } from "@/components/hv/Seo";
import { FadeIn } from "@/components/hv/Motion";
import { LogoMark } from "@/components/hv/Logo";
import { PasswordGate, useUnlocked } from "@/components/hv/PasswordGate";

const Sheet = () => (
  <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-24 hv-print-root" data-testid="runsheet-content">
    <FadeIn className="flex flex-wrap items-center justify-between gap-4 print:hidden">
      <Link to="/kit" data-testid="runsheet-back" className="inline-flex items-center gap-1.5 text-sm text-[#685F58] hover:text-[#1F1A17]"><ArrowLeft className="h-4 w-4" /> Back to kit</Link>
      <button type="button" onClick={() => window.print()} data-testid="runsheet-print-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-11 px-5 text-sm font-medium text-[#FBF8F4] transition-colors duration-300"><Printer className="h-4 w-4" /> Print run sheet</button>
    </FadeIn>
    <FadeIn delay={0.1} className="mt-10">
      <div className="flex items-center gap-3 text-[#B4532A]"><LogoMark className="h-6 w-6" /><span className="font-mono text-xs uppercase tracking-[0.25em]">Hearthside · {RUN_SHEET.date}</span></div>
      <h1 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tight text-[#1F1A17]" data-testid="runsheet-title">{RUN_SHEET.title}</h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#685F58]">{RUN_SHEET.intro}</p>
    </FadeIn>
    <div className="mt-10 space-y-10">
      {RUN_SHEET.blocks.map((b, bi) => (
        <FadeIn key={b.t} delay={0.15 + bi * 0.05} className="break-inside-avoid">
          <h2 className="flex items-baseline gap-3 font-serif text-2xl tracking-tight text-[#1F1A17]" data-testid={`runsheet-block-${bi}`}><span className="font-mono text-xs text-[#B4532A]">0{bi + 1}</span>{b.t}</h2>
          <ul className="mt-4 divide-y divide-[#E8DDD3] rounded-2xl border border-[#E8DDD3] bg-white print:border-[#1F1A17]">
            {b.items.map((it) => (
              <li key={it.time + it.what} className="grid grid-cols-[20px_64px_1fr_auto] items-start gap-3 px-4 py-3 text-sm">
                <span className="mt-0.5 h-4 w-4 rounded border border-[#1F1A17]/50" aria-hidden="true" />
                <span className="font-mono text-xs text-[#B4532A] pt-0.5">{it.time}</span>
                <span className="text-[#1F1A17] leading-relaxed">{it.what}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#685F58] pt-1">{it.where}</span>
              </li>
            ))}
          </ul>
        </FadeIn>
      ))}
    </div>
    <FadeIn delay={0.5} className="mt-12 rounded-[2rem] border border-[#E8DDD3] bg-white p-8 print:border-[#1F1A17]">
      <p className="font-serif text-xl leading-snug tracking-tight text-[#1F1A17]">{RUN_SHEET.footer}</p>
    </FadeIn>
  </div>
);

export default function RunSheetPage() {
  const { pw, setPw } = useUnlocked();
  return (
    <div data-testid="runsheet-page" className="min-h-[60vh]">
      <Seo title="Launch-day run sheet (private)" description="Hour-by-hour launch checklist." noindex />
      {pw ? <Sheet /> : <PasswordGate title="Launch-day run sheet" description="Private. Enter the password to open the hour-by-hour checklist." onUnlock={setPw} />}
    </div>
  );
}
